import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Module-level cache so switching back to a language already used this
// session (or a value already resolved by index.js before the first render
// — see `initialTranslations` below) never re-fetches its JSON chunk.
const translationsCache = {};

// Detect browser language - Spanish if es/es-*, English otherwise
export function detectLanguage() {
  const stored = localStorage.getItem('app-language');
  if (stored && (stored === 'es' || stored === 'en')) {
    return stored;
  }

  const browserLang = navigator.language || navigator.userLanguage || 'en';
  // Check if browser is Spanish (es, es-ES, es-MX, etc.)
  if (browserLang.startsWith('es')) {
    return 'es';
  }
  return 'en';
}

// Loads one locale's JSON on demand via a dynamic import(), so only the
// active language's ~27KB ever ships to a given visitor — previously
// en.json AND es.json were both statically imported (and therefore both
// bundled) unconditionally, even though only one is ever active per
// session. Cached per language so repeat calls are free.
export function loadLocale(language) {
  const lang = language === 'es' ? 'es' : 'en';
  if (translationsCache[lang]) {
    return Promise.resolve(translationsCache[lang]);
  }
  const importPromise = lang === 'es'
    ? import('./locales/es.json')
    : import('./locales/en.json');

  return importPromise.then((mod) => {
    const data = mod.default || mod;
    translationsCache[lang] = data;
    return data;
  });
}

// Nested key lookup (e.g., "nav.talk") in the given (already-loaded)
// translations object. Unlike the old dual-locale version of this function,
// there is no fallback to the other language's data here on a missing key —
// doing that would require always loading both locales, defeating the point
// of loading only the active one. en.json/es.json are expected to stay in
// key-parity (an existing project convention); a genuinely missing key
// falls back to returning the raw key, same as before.
function translate(data, key, params = {}) {
  if (!data) return key;

  const keys = key.split('.');
  let value = data;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      console.warn(`Translation missing: ${key}`);
      return key;
    }
  }

  // Replace parameters like {name} with actual values
  if (typeof value === 'string' && Object.keys(params).length > 0) {
    return value.replace(/\{(\w+)\}/g, (match, paramName) => {
      return params[paramName] !== undefined ? params[paramName] : match;
    });
  }

  return value || key;
}

export function LanguageProvider({ children, initialLanguage, initialTranslations }) {
  const [language, setLanguage] = useState(initialLanguage || detectLanguage);
  const [translations, setTranslations] = useState(
    initialTranslations || translationsCache[initialLanguage || detectLanguage()] || null
  );
  // The tutor's own words (its chat bubbles, and topic text sent to the voice
  // model) are always English, whatever the UI language — read via `tEn`
  // (TalkView.js, ConversationStartersView.js only; never on the logged-out
  // homepage). Loaded in the background below so it's ready by the time
  // either of those — already separate, lazy-loaded chunks — actually mount.
  const [enTranslations, setEnTranslations] = useState(() => {
    const initial = initialLanguage || detectLanguage();
    return initial === 'en' ? (initialTranslations || translationsCache.en || null) : (translationsCache.en || null);
  });

  useEffect(() => {
    let active = true;

    loadLocale(language).then((data) => {
      if (active) setTranslations(data);
    });

    if (language !== 'en') {
      loadLocale('en').then((data) => {
        if (active) setEnTranslations(data);
      });
    }

    return () => {
      active = false;
    };
  }, [language]);

  useEffect(() => {
    localStorage.setItem('app-language', language);
  }, [language]);

  // UI chrome (buttons, labels, headings): follows the chosen UI language.
  const t = (key, params = {}) => translate(translations, key, params);

  // The tutor's own words — see note on `enTranslations` above. Keys read
  // with tEn have no es.json entry on purpose.
  const tEn = (key, params = {}) => translate(enTranslations, key, params);

  const value = {
    language,
    setLanguage,
    t,
    tEn,
    isSpanish: language === 'es'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
